import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'sa-theme';

/** Read a previously persisted choice; `null` when unset or unavailable. */
const readStoredMode = (): ThemeMode | null => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
};

const systemPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/** First-visit default follows the OS; a persisted choice always wins. */
export const getInitialThemeMode = (): ThemeMode =>
  readStoredMode() ?? (systemPrefersDark() ? 'dark' : 'light');

const persistMode = (mode: ThemeMode): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* storage unavailable (private mode / SSR) — theme still works for the session */
  }
};

interface ThemeStore {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeStore>((set) => ({
  mode: getInitialThemeMode(),
  setMode: (mode) => {
    persistMode(mode);
    set({ mode });
  },
  toggle: () =>
    set((state) => {
      const mode: ThemeMode = state.mode === 'dark' ? 'light' : 'dark';
      persistMode(mode);
      return { mode };
    }),
}));
